import { useEffect, useState } from "react";
import { Send, CheckCircle2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import BackendServiceNotice from "@/components/BackendServiceNotice";
import {
  BUDGETS,
  initialContactForm,
  SERVICES,
  TIMELINES,
  YN,
  withCustomerContactDefaults,
} from "@/components/contact/contactConstants";
import { useCustomerContactProfile } from "@/components/contact/useCustomerContactProfile";
import ContactFormBrandMark from "@/components/contact/ContactFormBrandMark";
import "./ContactInquiryForm.css";

export default function ContactInquiryForm({
  className = "",
  onSubmitted,
  showSuccessActions = true,
  active = true,
  compact = false,
}) {
  const { profile, loading: profileLoading } = useCustomerContactProfile(active);
  const [form, setForm] = useState(initialContactForm);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!active || profileLoading) return;
    if (!profile) return;
    setForm((prev) => withCustomerContactDefaults(prev, profile));
  }, [active, profile, profileLoading]);

  const resetForm = () =>
    setForm(withCustomerContactDefaults(initialContactForm, profile));

  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const onFilesChange = (e) => {
    const incoming = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...incoming].slice(0, 6));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.company) {
      toast.error("회사명·이름·이메일·문의 내용은 필수입니다.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        fd.append(k, v);
      });
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/inquiries/with-files`, fd);
      setSubmitted(true);
      toast.success("문의가 정상 접수되었습니다. 확인 이메일을 발송했습니다.");
      resetForm();
      setFiles([]);
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err, "문의 접수 중 오류가 발생했습니다."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className={`contact-inquiry-form relative bg-[#0a0a0a] border border-white/10 rounded-2xl ${
        compact
          ? "contact-inquiry-form--compact p-4 md:p-5"
          : "p-7 md:p-10"
      } ${className}`}
    >
      {!submitted ? <ContactFormBrandMark variant="form" /> : null}
      <BackendServiceNotice className="mb-4" compact />
      {submitted ? (
        <div className="flex flex-col items-center justify-center text-center py-16">
          <CheckCircle2 size={56} className="text-emerald-400 mb-6" />
          <h3 className="font-display text-2xl font-medium text-white">접수 완료</h3>
          <p className="text-sm text-white/60 mt-3 max-w-md">
            소중한 문의를 보내주셔서 감사합니다. 확인 이메일을 발송했으며,
            담당자가 빠른 시일 내 회신드리겠습니다.
          </p>
          {showSuccessActions ? (
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              data-testid="contact-reset"
              className="btn-secondary mt-8"
            >
              새 문의 작성
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <SectionLabel compact={compact}>기본 정보</SectionLabel>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${compact ? "gap-2.5" : "gap-4"}`}>
            <Field label="회사명 *" compact={compact}>
              <input
                className="input"
                required
                value={form.company}
                onChange={update("company")}
                data-testid="contact-company"
                placeholder="DSiteSolution"
              />
            </Field>
            <Field label="담당자명 *">
              <input
                className="input"
                required
                value={form.name}
                onChange={update("name")}
                data-testid="contact-name"
                placeholder="홍길동"
              />
            </Field>
            <Field label="이메일 *">
              <input
                type="email"
                className="input"
                required
                value={form.email}
                onChange={update("email")}
                data-testid="contact-email"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="연락처">
              <input
                className="input"
                value={form.phone}
                onChange={update("phone")}
                data-testid="contact-phone"
                placeholder="010-0000-0000"
              />
            </Field>
          </div>

          <SectionLabel compact={compact}>프로젝트 정보</SectionLabel>
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${compact ? "gap-2.5" : "gap-4"}`}>
            <Field label="프로젝트 종류" compact={compact}>
              <select
                className="input"
                value={form.service_type}
                onChange={update("service_type")}
                data-testid="contact-service"
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="제작 목적" compact={compact}>
              <input
                className="input"
                value={form.project_purpose}
                onChange={update("project_purpose")}
                data-testid="contact-purpose"
                placeholder="예: 브랜드 인지도 향상, 매출 자동화"
              />
            </Field>
          </div>
          <div className={compact ? "mt-2.5" : "mt-4"}>
            <Field label="상세 요구사항" compact={compact}>
              <textarea
                rows={compact ? 2 : 3}
                className="input resize-none"
                value={form.requirements}
                onChange={update("requirements")}
                data-testid="contact-requirements"
                placeholder="필요한 기능이나 참고할 사이트가 있다면 자유롭게 작성해 주세요."
              />
            </Field>
          </div>

          <SectionLabel compact={compact}>
            {compact ? "보유 · 예산 · 일정" : "보유 현황"}
          </SectionLabel>
          <div
            className={`grid grid-cols-1 ${
              compact ? "sm:grid-cols-3 lg:grid-cols-5 gap-2.5" : "sm:grid-cols-3 gap-4"
            }`}
          >
            <Field label="도메인 보유" compact={compact}>
              <select
                className="input"
                value={form.has_domain}
                onChange={update("has_domain")}
                data-testid="contact-domain"
              >
                {YN.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="호스팅 보유" compact={compact}>
              <select
                className="input"
                value={form.has_hosting}
                onChange={update("has_hosting")}
                data-testid="contact-hosting"
              >
                {YN.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="기존 홈페이지" compact={compact}>
              <select
                className="input"
                value={form.has_existing_site}
                onChange={update("has_existing_site")}
                data-testid="contact-existing"
              >
                {YN.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </Field>
            {compact ? (
              <>
                <Field label="예산" compact={compact}>
                  <select
                    className="input"
                    value={form.budget}
                    onChange={update("budget")}
                    data-testid="contact-budget"
                  >
                    {BUDGETS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="희망 일정" compact={compact}>
                  <select
                    className="input"
                    value={form.timeline}
                    onChange={update("timeline")}
                    data-testid="contact-timeline"
                  >
                    {TIMELINES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            ) : null}
          </div>
          {form.has_existing_site === "true" && (
            <div className={compact ? "mt-2.5" : "mt-4"}>
              <Field label="기존 홈페이지 주소" compact={compact}>
                <input
                  className="input"
                  value={form.existing_site_url}
                  onChange={update("existing_site_url")}
                  placeholder="https://"
                />
              </Field>
            </div>
          )}

          {!compact ? (
            <>
              <SectionLabel>예산 · 일정</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="예산">
                  <select
                    className="input"
                    value={form.budget}
                    onChange={update("budget")}
                    data-testid="contact-budget"
                  >
                    {BUDGETS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="희망 일정">
                  <select
                    className="input"
                    value={form.timeline}
                    onChange={update("timeline")}
                    data-testid="contact-timeline"
                  >
                    {TIMELINES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </>
          ) : null}

          <SectionLabel compact={compact}>문의 내용 *</SectionLabel>
          <Field label="" compact={compact}>
            <textarea
              rows={compact ? 2 : 4}
              required
              className="input resize-none"
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message"
              placeholder="기획 단계의 아이디어도 환영합니다."
            />
          </Field>

          <div className={compact ? "mt-3 flex flex-col sm:flex-row sm:items-end gap-2.5" : ""}>
            <div className={compact ? "flex-1 min-w-0" : ""}>
              <SectionLabel compact={compact}>파일 첨부 (선택)</SectionLabel>
              <label
                className={`flex items-center gap-2 border border-dashed border-white/15 rounded-xl cursor-pointer hover:border-white/30 transition-colors text-white/60 ${
                  compact ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm rounded-xl"
                }`}
                data-testid="contact-file-label"
              >
                <Paperclip size={compact ? 12 : 14} />
                <span className="truncate">
                  {compact ? "참고자료 첨부 (최대 6개)" : "로고 · 기획서 · 참고자료 (최대 6개)"}
                </span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={onFilesChange}
                  data-testid="contact-files-input"
                />
              </label>
            </div>
            {compact ? (
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="btn-primary shrink-0 w-full sm:w-auto disabled:opacity-50"
              >
                {submitting ? "전송 중..." : "상담 신청 보내기"}
                <Send size={14} />
              </button>
            ) : null}
          </div>
          {files.length > 0 && (
            <div className={`flex flex-wrap gap-2 ${compact ? "mt-2" : "mt-3"}`}>
              {files.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white/80"
                >
                  <span className="truncate max-w-[180px]">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-white/40 hover:text-red-400"
                    aria-label="remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!compact ? (
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="btn-primary mt-8 w-full sm:w-auto disabled:opacity-50"
            >
              {submitting ? "전송 중..." : "상담 신청 보내기"}
              <Send size={14} />
            </button>
          ) : null}
          <p className={`text-white/40 ${compact ? "mt-2 text-[10px] leading-snug" : "mt-4 text-xs"}`}>
            제출 시 개인정보처리방침에 동의한 것으로 간주됩니다. 접수 즉시 자동 확인 이메일이
            발송됩니다.
          </p>
        </>
      )}
    </form>
  );
}

function Field({ label, children, compact = false }) {
  return (
    <label className="block">
      {label ? (
        <span
          className={`font-semibold uppercase tracking-[0.2em] text-white/40 block ${
            compact ? "text-[9px] mb-1" : "text-[10px] mb-2"
          }`}
        >
          {label}
        </span>
      ) : null}
      {children}
    </label>
  );
}

function SectionLabel({ children, compact = false }) {
  return (
    <div
      className={`flex items-center gap-2 first:mt-0 ${
        compact ? "mt-3 mb-1.5" : "mt-7 mb-3"
      }`}
    >
      <div className="w-4 h-px bg-white/20" />
      <span
        className={`font-semibold uppercase tracking-[0.25em] text-white/50 ${
          compact ? "text-[9px]" : "text-[10px]"
        }`}
      >
        {children}
      </span>
    </div>
  );
}
